import React, { useState, useEffect, useRef } from 'react';
import { useSharedState } from './SharedContext';
import api, { addSingleSlot, deleteAvailabilitySlot, removeAvailabilityForDate, moveBooking } from './api';
import { toast } from 'react-toastify';
import { Container, Row, Col, Form, Button, ListGroup, Card, Alert, Badge, Spinner, Modal } from 'react-bootstrap';

const AdminPage = ({ user }) => {
    const { selectedDate, setSelectedDate } = useSharedState();

    // State for schedule rules
    const [schedules, setSchedules] = useState([]);
    const [daysOfWeek, setDaysOfWeek] = useState([]);
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('17:00');
    const [duration, setDuration] = useState(30);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState('');
    const [loadingSchedules, setLoadingSchedules] = useState(true);

    // State for viewing and managing daily slots/bookings
    const [slots, setSlots] = useState([]);
    const [slotsError, setSlotsError] = useState('');
    const [listStyle, setListStyle] = useState({});
    const [loadingSlots, setLoadingSlots] = useState(true);

    // State for confirmation modal
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [itemToCancel, setItemToCancel] = useState(null);
    const [cancelType, setCancelType] = useState(''); // 'booking', 'schedule', 'slot', or 'day'

    // State for Add Slot Modal
    const [showAddSlotModal, setShowAddSlotModal] = useState(false);
    const [newSlotStartTime, setNewSlotStartTime] = useState('09:00');
    const [newSlotEndTime, setNewSlotEndTime] = useState('09:30');

    // State for Move Booking
    const [moveState, setMoveState] = useState({ isMoving: false, bookingToMove: null });


    const listContainerRef = useRef(null);

    const days = { 1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday", 7: "Sunday" };

    const fetchSchedules = async () => {
        setLoadingSchedules(true);
        try {
            const response = await api.get('/api/schedules');
            setSchedules(response.data);
        } catch (err) {
            toast.error('Failed to fetch schedules.');
        } finally {
            setLoadingSchedules(false);
        }
    };

    const fetchSlots = async (date) => {
        setLoadingSlots(true);
        setSlotsError('');
        try {
            const response = await api.get(`/api/availability?selected_date=${date}&_=${new Date().getTime()}`);
            setSlots(response.data);
        } catch (err) {
            setSlotsError('Failed to fetch slots for the selected date.');
            setSlots([]);
        } finally {
            setLoadingSlots(false);
        }
    };

    useEffect(() => {
        fetchSchedules();
        fetchSlots(selectedDate);
    }, [selectedDate]);

    useEffect(() => {
        if (startDate && !endDate) {
            const newEndDate = new Date(startDate);
            newEndDate.setMonth(newEndDate.getMonth() + 1);
            setEndDate(newEndDate.toISOString().split('T')[0]);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        const updateLayout = () => {
            if (window.innerWidth >= 992) { // lg breakpoint
                if (listContainerRef.current) {
                    const topOffset = listContainerRef.current.getBoundingClientRect().top;
                    const windowHeight = window.innerHeight;
                    const maxHeight = windowHeight - topOffset - 30; // 30px buffer
                    setListStyle({ maxHeight: `${maxHeight}px`, overflowY: 'auto' });
                }
            } else {
                setListStyle({ maxHeight: 'none', overflowY: 'visible' });
            }
        };

        updateLayout();
        window.addEventListener('resize', updateLayout);
        return () => window.removeEventListener('resize', updateLayout);
    }, [slots, schedules]); // Rerun when content changes that might affect height

    const resetScheduleForm = () => {
        setDaysOfWeek([]);
        setStartTime('09:00');
        setEndTime('17:00');
        setDuration(30);
        setStartDate(new Date().toISOString().split('T')[0]);
        setEndDate('');
    };

    const handleScheduleSubmit = async (e) => {
        e.preventDefault();

        if (daysOfWeek.length === 0) {
            toast.warn('Please select at least one day of the week.');
            return;
        }
        if (!startDate || !endDate) {
            toast.warn('Start Date and End Date are mandatory.');
            return;
        }
        if (new Date(endDate) < new Date(startDate)) {
            toast.error('End Date cannot be before the Start Date.');
            return;
        }
        if (startTime >= endTime) {
            toast.error('End Time must be after the Start Time.');
            return;
        }

        try {
            await api.post('/api/schedules', {
                days_of_week: daysOfWeek.map(day => parseInt(day)),
                start_time: startTime,
                end_time: endTime,
                slot_duration_minutes: parseInt(duration),
                start_date: startDate,
                end_date: endDate
            });
            toast.success('Schedule added successfully!');
            resetScheduleForm();
            fetchSchedules();
            fetchSlots(selectedDate); // Refresh slots
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to add schedule.');
        }
    };

    const confirmDeletion = (id, type) => {
        setCancelType(type);
        setItemToCancel(id);
        setShowConfirmModal(true);
    };

    const handleScheduleDelete = async () => {
        try {
            await api.delete(`/api/schedules/${itemToCancel}`);
            toast.success('Schedule deleted successfully.');
            fetchSchedules();
            fetchSlots(selectedDate); // Refresh slots
        } catch (err) {
            toast.error('Failed to delete schedule.');
        } finally {
            setShowConfirmModal(false);
        }
    };

    const handleCancelBooking = async () => {
        try {
            await api.delete(`/api/bookings/${itemToCancel}`);
            toast.success('Booking cancelled successfully.');
            fetchSlots(selectedDate); // Refresh slots for the current date
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to cancel booking.');
        } finally {
            setShowConfirmModal(false);
        }
    };

    const handleSlotDelete = async () => {
        try {
            await deleteAvailabilitySlot(itemToCancel);
            toast.success('Slot deleted successfully.');
            fetchSlots(selectedDate);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to delete slot.');
        } finally {
            setShowConfirmModal(false);
        }
    };

    const handleClearDay = async () => {
        try {
            await removeAvailabilityForDate(selectedDate);
            toast.success(`All available slots for ${selectedDate} have been cleared.`);
            fetchSlots(selectedDate);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to clear slots for the day.');
        } finally {
            setShowConfirmModal(false);
        }
    };


    const executeDeletion = () => {
        if (cancelType === 'booking') {
            handleCancelBooking();
        } else if (cancelType === 'schedule') {
            handleScheduleDelete();
        } else if (cancelType === 'slot') {
            handleSlotDelete();
        } else if (cancelType === 'day') {
            handleClearDay();
        }
    };

    const handleAddSlot = async (e) => {
        e.preventDefault();
        const startDateTime = `${selectedDate}T${newSlotStartTime}`;
        const endDateTime = `${selectedDate}T${newSlotEndTime}`;

        if (newSlotStartTime >= newSlotEndTime) {
            toast.error("End time must be after start time.");
            return;
        }

        try {
            await addSingleSlot({ start_time: startDateTime, end_time: endDateTime });
            toast.success("Slot added successfully!");
            setShowAddSlotModal(false);
            fetchSlots(selectedDate);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to add slot.");
        }
    };

    const startMove = (booking) => {
        setMoveState({ isMoving: true, bookingToMove: booking });
        toast.info(`Moving booking for ${booking.customer_name}. Select an available slot.`);
    };

    const cancelMove = () => {
        setMoveState({ isMoving: false, bookingToMove: null });
    };

    const executeMove = async (newSlotId) => {
        try {
            await moveBooking(moveState.bookingToMove.booking_id, newSlotId);
            toast.success(`Booking for ${moveState.bookingToMove.customer_name} moved successfully!`);
            fetchSlots(selectedDate);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to move booking.");
        } finally {
            cancelMove();
        }
    };
    
    const formatTime = (timeStr) => {
        return new Date(timeStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };


    return (
        <Container fluid className="pt-lg-3 px-lg-5 pb-lg-5">
            <Row>
                {/* Schedule Management Section */}
                <Col lg={7}>
                    <h2 className="mb-4">Schedule Management</h2>
                    <Row>
                        <Col md={6}>
                            <Card className="mb-4">
                                <Card.Header as="h5">Add New Schedule Block</Card.Header>
                                <Card.Body>
                                    <Form onSubmit={handleScheduleSubmit}>
                                        <Row>
                                            <Col><Form.Group className="mb-3"><Form.Label>Start Date</Form.Label><Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required /></Form.Group></Col>
                                            <Col><Form.Group className="mb-3"><Form.Label>End Date</Form.Label><Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} required /></Form.Group></Col>
                                        </Row>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Day of the Week</Form.Label>
                                            <Form.Control 
                                                as="select" 
                                                multiple 
                                                value={daysOfWeek} 
                                                onChange={e => setDaysOfWeek(Array.from(e.target.selectedOptions, option => option.value))}
                                                style={{ height: '180px' }}
                                            >
                                                {Object.entries(days).map(([value, name]) => (
                                                    <option key={value} value={value}>{name}</option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>
                                        <Form.Group className="mb-3"><Form.Label>Start Time</Form.Label><Form.Control type="time" value={startTime} onChange={e => setStartTime(e.target.value)} /></Form.Group>
                                        <Form.Group className="mb-3"><Form.Label>End Time</Form.Label><Form.Control type="time" value={endTime} onChange={e => setEndTime(e.target.value)} /></Form.Group>
                                        <Form.Group className="mb-3"><Form.Label>Slot Duration (minutes)</Form.Label><Form.Control type="number" value={duration} onChange={e => setDuration(e.target.value)} step="15" min="15" /></Form.Group>
                                        <Button variant="primary" type="submit">Add Schedule</Button>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="mb-4">
                                <Card.Header as="h5">Current Schedule Rules</Card.Header>
                                <ListGroup ref={listContainerRef} variant="flush" style={listStyle}>
                                    {loadingSchedules ? (
                                        <ListGroup.Item className="d-flex justify-content-center">
                                            <Spinner animation="border" />
                                        </ListGroup.Item>
                                    ) : schedules.length > 0 ? schedules.map(s => (
                                        <ListGroup.Item key={s.id} className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <p className="mb-1"><strong>{days[s.day_of_week]}s</strong> from <strong>{s.start_date}</strong> to <strong>{s.end_date}</strong></p>
                                                <p className="mb-0 text-muted">{s.start_time} - {s.end_time} ({s.slot_duration_minutes} min slots)</p>
                                            </div>
                                            <Button variant="outline-danger" size="sm" onClick={() => confirmDeletion(s.id, 'schedule')}>Delete</Button>
                                        </ListGroup.Item>
                                    )) : <ListGroup.Item>No schedules defined.</ListGroup.Item>}
                                </ListGroup>
                            </Card>
                        </Col>
                    </Row>
                </Col>

                {/* Daily Slot and Booking Management Section */}
                <Col lg={5}>
                    <h2 className="mb-4">Daily Availability Editor</h2>
                    <Card>
                        <Card.Header>
                            <Form.Group as={Row} className="align-items-center g-2">
                                <Form.Label column sm="auto"><strong>Select Date:</strong></Form.Label>
                                <Col sm="auto">
                                    <Form.Control type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} />
                                </Col>
                                <Col sm="auto"><span className="text-muted">{new Date(selectedDate.replace(/-/g, '/')).toLocaleDateString('en-US', { weekday: 'long' })}</span></Col>
                            </Form.Group>
                        </Card.Header>
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <Button variant="success" size="sm" className="me-2" onClick={() => setShowAddSlotModal(true)}>+ Add Slot</Button>
                                    <Button variant="danger" size="sm" onClick={() => confirmDeletion(null, 'day')}>Clear Day</Button>
                                </div>
                                {moveState.isMoving && (
                                    <Button variant="secondary" size="sm" onClick={cancelMove}>Cancel Move</Button>
                                )}
                            </div>
                            {slotsError && <Alert variant="danger">{slotsError}</Alert>}
                            <ListGroup variant="flush" style={listStyle}>
                                {loadingSlots ? (
                                    <ListGroup.Item className="d-flex justify-content-center">
                                        <Spinner animation="border" />
                                    </ListGroup.Item>
                                ) : slots.length > 0 ? slots.map((slot, index) => (
                                    <ListGroup.Item 
                                        key={index} 
                                        className={`d-flex justify-content-between align-items-center ${slot.booked ? 'bg-light' : ''} ${moveState.isMoving && moveState.bookingToMove.booking_id === slot.booking_id ? 'border border-primary' : ''}`}
                                    >
                                        <div>
                                            {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                                            {slot.booked && (
                                                <Badge bg="primary" className="ms-2">
                                                    Booked by {slot.customer_name}
                                                </Badge>
                                            )}
                                        </div>
                                        {slot.booked ? (
                                            <div>
                                                <Button variant="outline-primary" size="sm" className="me-2" onClick={() => startMove(slot)}>Move</Button>
                                                <Button variant="outline-warning" size="sm" onClick={() => confirmDeletion(slot.booking_id, 'booking')}>
                                                    Cancel
                                                </Button>
                                            </div>
                                        ) : moveState.isMoving ? (
                                            <Button variant="info" size="sm" onClick={() => executeMove(slot.slot_id)}>
                                                Move Here
                                            </Button>
                                        ) : (
                                            <Button variant="outline-danger" size="sm" onClick={() => confirmDeletion(slot.slot_id, 'slot')}>
                                                Delete Slot
                                            </Button>
                                        )}
                                    </ListGroup.Item>
                                )) : <ListGroup.Item>No slots scheduled for this day.</ListGroup.Item>}
                            </ListGroup>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Action</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this {cancelType}? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Close
                    </Button>
                    <Button variant="danger" onClick={executeDeletion}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showAddSlotModal} onHide={() => setShowAddSlotModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Add a New Slot for {selectedDate}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddSlot}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Start Time</Form.Label>
                            <Form.Control type="time" value={newSlotStartTime} onChange={e => setNewSlotStartTime(e.target.value)} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>End Time</Form.Label>
                            <Form.Control type="time" value={newSlotEndTime} onChange={e => setNewSlotEndTime(e.target.value)} required />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddSlotModal(false)}>Close</Button>
                        <Button variant="primary" type="submit">Add Slot</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default AdminPage;
